import { useEffect, useState } from "react";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import { toast } from "sonner";
import { Calendar as CalendarIcon, Plus, Pencil, Trash2, MapPin, Clock, Tag, X, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import {
  fetchPerformances,
  createPerformance,
  updatePerformance,
  deletePerformance,
} from "@/lib/api";

const EMPTY = { date: "", venue: "", location: "", time: "", type: "", notes: "" };

export default function AdminPerformances({ token }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = closed, "new" or perf object
  const [form, setForm] = useState(EMPTY);
  const [date, setDate] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setList(await fetchPerformances());
    } catch {
      toast.error("Laden van optredens mislukt");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing("new");
    setForm({ ...EMPTY, type: "Tafelgoochelaar" });
    setDate(null);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      date: p.date,
      venue: p.venue || "",
      location: p.location || "",
      time: p.time || "",
      type: p.type || "",
      notes: p.notes || "",
    });
    setDate(p.date ? new Date(p.date) : null);
  };
  const close = () => {
    setEditing(null);
    setForm(EMPTY);
    setDate(null);
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!date) {
      toast.error("Kies een datum");
      return;
    }
    if (!form.venue.trim()) {
      toast.error("Vul een locatie/venue in");
      return;
    }
    setSaving(true);
    const payload = {
      date: format(date, "yyyy-MM-dd"),
      venue: form.venue.trim(),
      location: form.location.trim() || null,
      time: form.time.trim() || null,
      type: form.type.trim() || null,
      notes: form.notes.trim() || null,
    };
    try {
      if (editing === "new") {
        const created = await createPerformance(payload, token);
        setList((l) => [...l, created].sort((a, b) => a.date.localeCompare(b.date)));
        toast.success("Optreden toegevoegd");
      } else {
        const updated = await updatePerformance(editing.id, payload, token);
        setList((l) =>
          l.map((p) => (p.id === updated.id ? updated : p)).sort((a, b) => a.date.localeCompare(b.date))
        );
        toast.success("Optreden bijgewerkt");
      }
      close();
    } catch {
      toast.error("Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Optreden op ${p.date} definitief verwijderen?`)) return;
    try {
      await deletePerformance(p.id, token);
      setList((l) => l.filter((x) => x.id !== p.id));
      toast.success("Verwijderd");
    } catch {
      toast.error("Verwijderen mislukt");
    }
  };

  return (
    <section className="mb-12" data-testid="admin-performances-section">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <CalendarIcon className="w-5 h-5 text-gold" />
          <h2 className="font-display text-2xl font-bold">Optredens / Agenda</h2>
          <span className="text-xs text-[#7A6C96] uppercase tracking-widest">
            {list.length} {list.length === 1 ? "optreden" : "optredens"}
          </span>
        </div>
        <Button
          onClick={openNew}
          className="bg-gold text-night-deep hover:bg-gold-light font-semibold rounded-full"
          data-testid="add-performance-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nieuw optreden
        </Button>
      </div>

      {loading ? (
        <div className="glass rounded-2xl p-8 text-center text-[#7A6C96]">Laden...</div>
      ) : list.length === 0 ? (
        <div className="glass rounded-2xl p-8 text-center text-[#7A6C96]">
          Nog geen optredens. Klik op "Nieuw optreden" om er een toe te voegen.
        </div>
      ) : (
        <ul className="space-y-3" data-testid="admin-performances-list">
          {list.map((p) => {
            const d = new Date(p.date);
            return (
              <li
                key={p.id}
                className="group glass rounded-xl p-4 flex items-center justify-between gap-4 hover:border-gold/40 transition-colors"
                data-testid={`perf-row-${p.id}`}
              >
                <div className="flex items-center gap-4 min-w-0 flex-1">
                  <div className="text-center bg-night-surface/60 rounded-lg px-3 py-2 shrink-0">
                    <div className="font-display text-2xl font-bold gold-text leading-none">
                      {format(d, "d", { locale: nl })}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-[#B5A9C9] mt-0.5">
                      {format(d, "MMM", { locale: nl }).replace(".", "")}
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-display text-base font-semibold truncate">{p.venue}</div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#B5A9C9] mt-1">
                      <span>{format(d, "EEEE d MMM yyyy", { locale: nl })}</span>
                      {p.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{p.location}</span>}
                      {p.time && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{p.time}</span>}
                      {p.type && <span className="inline-flex items-center gap-1"><Tag className="w-3 h-3" />{p.type}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    onClick={() => openEdit(p)}
                    variant="ghost"
                    size="sm"
                    className="text-[#B5A9C9] hover:text-gold hover:bg-gold/10"
                    data-testid={`perf-edit-${p.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => handleDelete(p)}
                    variant="ghost"
                    size="sm"
                    className="text-red-400/70 hover:text-red-400 hover:bg-red-500/10"
                    data-testid={`perf-delete-${p.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {editing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={close}
          data-testid="perf-modal"
        >
          <div
            className="glass-strong rounded-2xl p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-6">
              <h3 className="font-display text-2xl font-bold">
                {editing === "new" ? "Nieuw optreden" : "Optreden bewerken"}
              </h3>
              <button
                onClick={close}
                className="text-[#7A6C96] hover:text-gold"
                aria-label="Sluiten"
                data-testid="perf-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <FormRow label="Datum" required>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      data-testid="perf-date-trigger"
                      className={cn(
                        "w-full justify-start font-normal bg-night-surface/60 border-gold/25 text-[#F8F5FF] hover:bg-mystic/10 h-11",
                        !date && "text-[#7A6C96]"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 text-gold" />
                      {date ? format(date, "PPP", { locale: nl }) : "Kies datum"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-night-surface border-gold/30" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      locale={nl}
                      initialFocus
                      className="bg-night-surface text-[#F8F5FF]"
                    />
                  </PopoverContent>
                </Popover>
              </FormRow>

              <FormRow label="Venue / Locatie" required>
                <Input
                  value={form.venue}
                  onChange={(e) => update("venue", e.target.value)}
                  placeholder="Bv. Center Parcs Erperheide"
                  className="bg-night-surface/60 border-gold/25 text-[#F8F5FF] h-11"
                  data-testid="perf-input-venue"
                />
              </FormRow>

              <FormRow label="Stad / Plaats">
                <Input
                  value={form.location}
                  onChange={(e) => update("location", e.target.value)}
                  placeholder="Bv. Peer, België"
                  className="bg-night-surface/60 border-gold/25 text-[#F8F5FF] h-11"
                  data-testid="perf-input-location"
                />
              </FormRow>

              <div className="grid grid-cols-2 gap-4">
                <FormRow label="Tijd">
                  <Input
                    value={form.time}
                    onChange={(e) => update("time", e.target.value)}
                    placeholder="Bv. 19:00 – 21:00"
                    className="bg-night-surface/60 border-gold/25 text-[#F8F5FF] h-11"
                    data-testid="perf-input-time"
                  />
                </FormRow>
                <FormRow label="Type show">
                  <Input
                    value={form.type}
                    onChange={(e) => update("type", e.target.value)}
                    placeholder="Bv. Tafelgoochelaar"
                    className="bg-night-surface/60 border-gold/25 text-[#F8F5FF] h-11"
                    data-testid="perf-input-type"
                  />
                </FormRow>
              </div>

              <FormRow label="Interne notitie (niet zichtbaar voor publiek)">
                <Input
                  value={form.notes}
                  onChange={(e) => update("notes", e.target.value)}
                  placeholder="Optioneel"
                  className="bg-night-surface/60 border-gold/25 text-[#F8F5FF] h-11"
                  data-testid="perf-input-notes"
                />
              </FormRow>
            </div>

            <div className="flex items-center justify-end gap-3 mt-8">
              <Button
                onClick={close}
                variant="ghost"
                className="text-[#B5A9C9] hover:bg-night-surface"
                data-testid="perf-cancel"
              >
                Annuleren
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-gold text-night-deep hover:bg-gold-light font-semibold"
                data-testid="perf-save"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Opslaan..." : "Opslaan"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function FormRow({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-widest text-[#B5A9C9]">
        {label}
        {required && <span className="text-gold ml-1">*</span>}
      </Label>
      {children}
    </div>
  );
}
